import { useState } from 'react';

const PLATFORMS = [
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Compatible with QuickBooks Online CSV import',
    headers: ['Date', 'Description', 'Customer', 'Product/Service', 'Amount', 'Status'],
    row: (entry) => [
      formatDate(entry.invoice_date, 'MM/DD/YYYY'),
      entry.project || '',
      entry.client || '',
      entry.type || '',
      entry.amount || 0,
      entry.status || '',
    ],
  },
  {
    id: 'freshbooks',
    name: 'FreshBooks',
    description: 'Compatible with FreshBooks invoice import',
    headers: ['Date', 'Client Name', 'Description', 'Amount', 'Status'],
    row: (entry) => [
      formatDate(entry.invoice_date, 'YYYY-MM-DD'),
      entry.client || '',
      entry.project || '',
      entry.amount || 0,
      entry.status || '',
    ],
  },
  {
    id: 'wave',
    name: 'Wave',
    description: 'Compatible with Wave Accounting CSV import',
    headers: ['Transaction Date', 'Description', 'Account', 'Amount', 'Status'],
    row: (entry) => [
      formatDate(entry.invoice_date, 'YYYY-MM-DD'),
      entry.project || entry.client || '',
      entry.type || 'Income',
      entry.amount || 0,
      entry.status || '',
    ],
  },
  {
    id: 'bonsai',
    name: 'Bonsai',
    description: 'Compatible with Bonsai records import',
    headers: ['Date', 'Client', 'Project', 'Type', 'Amount', 'Status'],
    row: (entry) => [
      formatDate(entry.invoice_date, 'MM/DD/YYYY'),
      entry.client || '',
      entry.project || '',
      entry.type || '',
      entry.amount || 0,
      entry.status || '',
    ],
  },
];

function formatDate(dateStr, format) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  if (format === 'MM/DD/YYYY') return `${mm}/${dd}/${yyyy}`;
  if (format === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
  return dateStr;
}

function buildCSV(platform, entries) {
  const lines = [platform.headers.join(',')];
  entries.forEach(entry => {
    const row = platform.row(entry).map(val => {
      const str = String(val);
      // Wrap in quotes if contains comma, quote, or newline
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    });
    lines.push(row.join(','));
  });
  return lines.join('\n');
}

function downloadCSV(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ExportModal({ entries, filterLabel, theme, onClose }) {
  const [selected, setSelected] = useState('quickbooks');

  const t = theme || {};

  function handleExport() {
    const platform = PLATFORMS.find(p => p.id === selected);
    const csv = buildCSV(platform, entries);
    const dateStamp = new Date().toISOString().slice(0, 10);
    downloadCSV(`writer-os-income-${platform.id}-${dateStamp}.csv`, csv);
    onClose();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: t.surface || '#ffffff',
        border: `1px solid ${t.border || '#e0d8c8'}`,
        borderRadius: '16px',
        width: '100%', maxWidth: '460px',
        padding: '32px',
        color: t.text || '#0a2342',
        boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '6px' }}>
            Export Income Data
          </div>
          <div style={{ fontSize: '13px', opacity: 0.55 }}>
            Exporting {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
            {filterLabel ? ` · ${filterLabel}` : ''}
          </div>
        </div>

        {/* Platform picker */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
          {PLATFORMS.map(platform => {
            const active = selected === platform.id;
            return (
              <div
                key={platform.id}
                onClick={() => setSelected(platform.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px 16px', borderRadius: '10px',
                  background: active ? (t.accent ? `${t.accent}18` : '#d9ad2b18') : (t.surfaceAlt || '#f5f0e8'),
                  border: `1px solid ${active ? (t.accent || '#d9ad2b') : (t.border || '#e0d8c8')}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {/* Radio dot */}
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${active ? (t.accent || '#d9ad2b') : (t.border || '#e0d8c8')}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                  {active && (
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: t.accent || '#d9ad2b',
                    }} />
                  )}
                </div>

                {/* Text */}
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{platform.name}</div>
                  <div style={{ fontSize: '12px', opacity: 0.45, marginTop: '1px' }}>{platform.description}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: `1px solid ${t.border || '#e0d8c8'}`,
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              color: t.text || '#0a2342',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            style={{
              background: t.accent || '#d9ad2b',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Download CSV →
          </button>
        </div>
      </div>
    </div>
  );
}